import { useEffect, useState } from "react";
import { adminListLanguages, adminCollectionTranslatableFields, adminTranslateCollectionEntry, adminSaveCollectionTranslation } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, Globe, ChevronDown } from "lucide-react";

const LABELS = {
  title: "Title", subtitle: "Subtitle", summary: "Summary", body: "Body", points: "Key points",
  name: "Name", description: "Description", significance: "Significance", epithet: "Epithet", domain: "Domain",
  era: "Era", location: "Location", region: "Region", state: "State", deity: "Deity", timings: "Timings",
  aarti_timings: "Aarti timings", pooja_info: "Pooja info", darshan_info: "Darshan info",
  highlights: "Highlights", festivals: "Festivals",
};

// Renders an editable value by type: string → textarea, string[] → newline textarea,
// object[]/other → JSON textarea. Returns { el } via render prop.
function FieldEditor({ k, value, onChange }) {
  const isStrArr = Array.isArray(value) && value.every((x) => typeof x === "string");
  const isObjArr = Array.isArray(value) && !isStrArr;
  let text, parse;
  if (isStrArr) { text = value.join("\n"); parse = (t) => t.split("\n").map((x) => x.trim()).filter(Boolean); }
  else if (isObjArr) { text = JSON.stringify(value, null, 2); parse = (t) => { try { return JSON.parse(t); } catch { return value; } }; }
  else { text = value == null ? "" : String(value); parse = (t) => t; }
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-3">{LABELS[k] || k}{isObjArr ? " (JSON)" : isStrArr ? " (one per line)" : ""}</span>
      <textarea
        value={text}
        onChange={(e) => onChange(parse(e.target.value))}
        rows={isObjArr ? 5 : (text && text.length > 90 ? 3 : 1)}
        className="w-full resize-y border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none"
      />
    </label>
  );
}

// Per-entry translation panel for flat collections (Hub / Deities / Temples).
// Select a language → auto-translate with AI or hand-enter translations, then save.
export default function CollectionTranslations({ name, itemId, i18n = {}, onChange }) {
  const [langs, setLangs] = useState([]);
  const [fields, setFields] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    adminListLanguages().then((all) => setLangs((all || []).filter((l) => !l.system))).catch(() => {});
    adminCollectionTranslatableFields(name).then((d) => setFields(d.fields || [])).catch(() => {});
  }, [name]);

  if (!itemId) return null;

  const setTr = (code, tr) => onChange({ ...i18n, [code]: tr });

  const autoTranslate = async (code) => {
    setBusy(code);
    try {
      const r = await adminTranslateCollectionEntry(name, itemId, code);
      setTr(code, r.translation || {});
      setOpen(code);
      toast.success("Translated — review the draft and save.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Translation failed."); }
    finally { setBusy(""); }
  };

  const save = async (code) => {
    setBusy(code);
    try {
      const r = await adminSaveCollectionTranslation(name, itemId, code, i18n[code] || {});
      setTr(code, r.translation || {});
      toast.success("Translation saved — live on the site for this language.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(""); }
  };

  const startManual = (code) => {
    if (!i18n[code]) {
      const blank = {};
      fields.forEach((f) => { blank[f] = ""; });
      setTr(code, blank);
    }
    setOpen(open === code ? null : code);
  };

  return (
    <div className="mt-8 border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.05)] p-5 sm:p-6" data-testid="collection-translations">
      <div className="mb-1 flex items-center gap-2">
        <Globe className="h-4 w-4 text-gold" />
        <h3 className="font-serif-latin text-lg text-ink">Translations</h3>
      </div>
      <p className="mb-4 text-xs text-ink-3">Pick a language, auto-translate with AI or type the translation yourself, then save. Published instantly for readers in that language.</p>
      {langs.length === 0 && <p className="text-sm text-ink-3">No extra languages yet. Add one under Admin → Languages.</p>}
      <div className="space-y-2">
        {langs.map((l) => {
          const tr = i18n[l.code];
          const translated = tr && Object.values(tr).some((v) => (Array.isArray(v) ? v.length : String(v || "").trim()));
          return (
            <div key={l.code} data-testid={`ctr-${l.code}`} className="border border-line bg-surface">
              <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="flex items-center gap-2">
                  <span className="font-serif-latin text-ink">{l.native_label || l.label}</span>
                  <span className="text-xs text-ink-3">· {l.code}</span>
                  {translated
                    ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-green-700"><Check className="h-3 w-3" />translated</span>
                    : <span className="text-[10px] uppercase tracking-wide text-ink-3">not translated</span>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid={`ctr-auto-${l.code}`} onClick={() => autoTranslate(l.code)} disabled={busy === l.code} className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-50">
                    {busy === l.code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {translated ? "Re-translate" : "Auto-translate"}
                  </button>
                  <button data-testid={`ctr-edit-${l.code}`} onClick={() => startManual(l.code)} className="inline-flex items-center gap-1 border border-line px-3 py-1.5 text-xs text-ink-2 transition-colors hover:text-ink">
                    {tr ? "Edit" : "Enter manually"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === l.code ? "rotate-180" : ""}`} />
                  </button>
                </div>
              </div>
              {tr && open === l.code && (
                <div className="space-y-3 border-t border-line p-4">
                  {(fields.length ? fields : Object.keys(tr)).map((f) => (
                    <FieldEditor key={f} k={f} value={tr[f]} onChange={(v) => setTr(l.code, { ...tr, [f]: v })} />
                  ))}
                  <button data-testid={`ctr-save-${l.code}`} onClick={() => save(l.code)} disabled={busy === l.code} className="mt-2 inline-flex items-center gap-2 bg-[var(--saffron)] px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
                    {busy === l.code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save translation
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
